import React, { useState } from 'react';
import { Database, CheckCircle, XCircle, Download, Copy, Cloud } from 'lucide-react';
import { localDbService } from '../../services/localDatabase';
import { apiService } from '../../services/api';
import { inventarioInicial } from '../../data/inventarioInicial';
import { exportInventoryToJSON, copyInventoryToClipboard } from '../../utils/exportInventoryData';

const DatabaseMigration: React.FC = () => {
  const [migrating, setMigrating] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleMigration = async () => {
    setMigrating(true);
    setStatus('idle');
    setMessage('');

    try {
      const hasData = await localDbService.hasData();
      
      if (hasData) {
        const confirm = window.confirm('¿Desea reemplazar los datos existentes con el inventario inicial?');
        if (!confirm) {
          setMessage('Migración cancelada por el usuario');
          setMigrating(false);
          return;
        }
        await localDbService.clearDatabase();
      }
      
      await localDbService.bulkInsertInventory(inventarioInicial);
      setStatus('success');
      setMessage(`Migración completada exitosamente. ${inventarioInicial.length} productos cargados en la base de datos local.`);
    } catch (error) {
      setStatus('error');
      setMessage(`Error durante la migración: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setMigrating(false);
    }
  };

  const handleExport = () => {
    const data = localDbService.exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alere-inventory-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMigrateToMongoDB = async () => {
    setMigrating(true);
    setStatus('idle');
    setMessage('');

    try {
      // Verificar si el API está disponible
      const isApiAvailable = await apiService.healthCheck();
      if (!isApiAvailable) {
        throw new Error('El servidor backend no está disponible');
      }

      // Migrar datos a MongoDB
      const insertedCount = await apiService.bulkInsertInventory(inventarioInicial);
      setStatus('success');
      setMessage(`Migración a MongoDB completada: ${insertedCount} productos cargados en la base de datos.`);
    } catch (error) {
      setStatus('error');
      setMessage(`Error durante la migración a MongoDB: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-gray/20 p-6">
      <div className="flex items-center mb-4">
        <Database className="text-warm-brown mr-2" size={24} />
        <h3 className="text-lg font-semibold text-dark-text font-heading">
          Gestión de Base de Datos
        </h3>
      </div>

      <p className="text-medium-gray mb-4">
        Esta herramienta gestiona el inventario en la base de datos local del navegador.
      </p>

      <div className="space-y-4">
        <div className="flex space-x-4">
          <button
            onClick={handleMigration}
            disabled={migrating}
            className={`
              flex items-center px-4 py-2 rounded-lg font-medium transition-colors
              ${migrating 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-accent text-white hover:bg-warm-brown'
              }
            `}
          >
            <Database className="mr-2" size={18} />
            {migrating ? 'Migrando...' : 'Cargar a Base Local'}
          </button>

          <button
            onClick={handleMigrateToMongoDB}
            disabled={migrating}
            className={`
              flex items-center px-4 py-2 rounded-lg font-medium transition-colors
              ${migrating 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-green-600 text-white hover:bg-green-700'
              }
            `}
          >
            <Cloud className="mr-2" size={18} />
            {migrating ? 'Migrando...' : 'Migrar a MongoDB'}
          </button>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 rounded-lg font-medium transition-colors bg-warm-brown text-white hover:bg-accent"
          >
            <Download className="mr-2" size={18} />
            Exportar Backup
          </button>

          <button
            onClick={exportInventoryToJSON}
            className="flex items-center px-4 py-2 rounded-lg font-medium transition-colors bg-blue-600 text-white hover:bg-blue-700"
          >
            <Download className="mr-2" size={18} />
            Exportar JSON
          </button>

          <button
            onClick={copyInventoryToClipboard}
            className="flex items-center px-4 py-2 rounded-lg font-medium transition-colors bg-purple-600 text-white hover:bg-purple-700"
          >
            <Copy className="mr-2" size={18} />
            Copiar JSON
          </button>
        </div>
      </div>

      {status !== 'idle' && (
        <div className={`
          mt-4 p-4 rounded-lg flex items-start
          ${status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}
        `}>
          {status === 'success' ? (
            <CheckCircle className="mr-2 mt-0.5" size={20} />
          ) : (
            <XCircle className="mr-2 mt-0.5" size={20} />
          )}
          <span className="text-sm">{message}</span>
        </div>
      )}

      <div className="mt-6 p-4 bg-cream rounded-lg">
        <h4 className="font-medium text-dark-text mb-2">Información:</h4>
        <ul className="text-sm text-medium-gray space-y-1">
          <li>• <strong>Base Local</strong>: Almacena en el navegador (sin sincronización)</li>
          <li>• <strong>MongoDB</strong>: Almacena en la nube (sincronizado entre dispositivos)</li>
          <li>• <strong>Exportar JSON</strong>: Descarga el archivo inventarioInicial.json</li>
          <li>• <strong>Copiar JSON</strong>: Copia los datos al portapapeles</li>
          <li>• Usa los datos exportados para el script de migración en el backend</li>
        </ul>
      </div>
    </div>
  );
};

export default DatabaseMigration;